import type { EnterpriseUserUsage } from '@/api/usage'
import { downloadBlob, escapeCSV } from '@/utils/csv'

const isValidDate = (date: Date): boolean => !isNaN(date.getTime())

const formatDateKey = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const getWeekStartDate = (date: Date): Date => {
  const weekStart = new Date(date)
  const dayOfWeek = weekStart.getDay()
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  weekStart.setDate(weekStart.getDate() - daysToMonday)
  weekStart.setHours(0, 0, 0, 0)
  return weekStart
}

export const getWeekKeyFromDay = (dayStr: string): string | null => {
  try {
    const date = new Date(`${dayStr}T00:00:00`)

    if (!isValidDate(date)) {
      return null
    }

    return formatDateKey(getWeekStartDate(date))
  } catch {
    return null
  }
}

export const getCurrentWeekKey = (referenceDate: Date = new Date()): string =>
  formatDateKey(getWeekStartDate(referenceDate))

export const formatWeekRange = (weekStartStr: string, locale = 'en-US'): string => {
  try {
    const monday = new Date(`${weekStartStr}T00:00:00`)

    if (!isValidDate(monday)) {
      return weekStartStr
    }

    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)

    const startStr = monday.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
    const endStr =
      sunday.getFullYear() !== monday.getFullYear()
        ? sunday.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })
        : sunday.toLocaleDateString(locale, { month: 'short', day: 'numeric' })

    return `${startStr} - ${endStr}`
  } catch {
    return weekStartStr
  }
}

/**
 * Format week string (YYYY-MM-DD) to readable date with first 3 letters of month
 * Example: "2025-11-23" -> "Nov 23, 2025"
 */
export const formatWeekToDate = (weekStr: string, locale = 'en-US'): string => {
  try {
    const date = new Date(weekStr)

    if (isValidDate(date)) {
      const monthFull = date.toLocaleDateString(locale, { month: 'long' })
      const monthShort = monthFull.substring(0, 3)
      const day = date.getDate()
      const year = date.getFullYear()
      return `${monthShort} ${day}, ${year}`
    }

    return weekStr
  } catch {
    return weekStr
  }
}

/**
 * Format timestamp to relative date string
 */
export const formatLastUsed = (timestamp: string | null, locale = 'en-US'): string => {
  if (!timestamp) return 'N/A'

  const date = new Date(timestamp)
  const now = new Date()

  // Reset time to midnight for accurate day comparison
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  const diffMs = nowOnly.getTime() - dateOnly.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  // Handle future dates or today
  if (diffDays <= 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays >= 2 && diffDays <= 7) return `${diffDays} days ago`

  return date.toLocaleDateString(locale)
}

/**
 * Calculate trend based on visible weekly data
 */
export const calculateTrend = (
  visibleWeeklyData: Array<{ week: string; wordcount: number }>
): { percentage: number; direction: 'up' | 'down' | 'neutral'; isPositive: boolean } => {
  if (visibleWeeklyData.length < 3) {
    return { percentage: 0, direction: 'neutral' as const, isPositive: false }
  }

  // Compare last week (index 1) to 2 weeks ago (index 2) in the visible range
  const lastWeek = visibleWeeklyData[1]?.wordcount || 0
  const twoWeeksAgo = visibleWeeklyData[2]?.wordcount || 0

  if (twoWeeksAgo === 0) {
    return { percentage: 0, direction: 'neutral' as const, isPositive: false }
  }

  const percentageChange = ((lastWeek - twoWeeksAgo) / twoWeeksAgo) * 100
  const isPositive = percentageChange > 0

  return {
    percentage: Math.abs(percentageChange),
    direction: isPositive ? ('up' as const) : ('down' as const),
    isPositive,
  }
}

/**
 * Get date range string from visible weekly data
 * Returns a formatted date range like "Nov 2, 2025 - Nov 23, 2025"
 */
export const getDateRange = (visibleWeeklyData: Array<{ week: string; wordcount: number }>): string => {
  if (visibleWeeklyData.length === 0) {
    return 'No data available'
  }

  const weeks = visibleWeeklyData
    .map((w) => w.week)
    .filter(Boolean)
    .sort()
  if (weeks.length === 0) {
    return 'No data available'
  }

  const oldestWeek = weeks[0]
  const newestWeek = weeks[weeks.length - 1]

  if (oldestWeek === newestWeek) {
    return formatWeekToDate(newestWeek)
  }

  return `${formatWeekToDate(oldestWeek)} - ${formatWeekToDate(newestWeek)}`
}

/**
 * Compute all possible weeks by generating a range from furthest future to furthest past
 * @param members - Array of members with daily_word_counts
 * @returns Array of week strings in YYYY-MM-DD format, sorted from most recent to oldest
 */
export const computeAllPossibleWeeks = (
  members: Array<{ daily_word_counts?: Array<{ day?: string; wordcount: number }> }>
): string[] => {
  // Find the earliest and latest dates from all members' daily data
  let earliestDay: string | null = null
  let latestDay: string | null = null

  members.forEach((member) => {
    member.daily_word_counts?.forEach((dayData) => {
      if (dayData.day) {
        if (!earliestDay || dayData.day < earliestDay) {
          earliestDay = dayData.day
        }
        if (!latestDay || dayData.day > latestDay) {
          latestDay = dayData.day
        }
      }
    })
  })

  if (!earliestDay || !latestDay) {
    return []
  }

  const latestWeek = getWeekKeyFromDay(latestDay)
  const earliestWeek = getWeekKeyFromDay(earliestDay)

  if (!latestWeek || !earliestWeek) {
    return []
  }

  const latestDate = new Date(`${latestWeek}T00:00:00`)
  const earliestDate = new Date(`${earliestWeek}T00:00:00`)

  // Generate all weeks from latest to earliest, stepping back 7 days at a time
  const weeks: string[] = []
  const currentDate = new Date(latestDate)

  while (currentDate >= earliestDate) {
    weeks.push(formatDateKey(currentDate))

    // Go back 7 days
    currentDate.setDate(currentDate.getDate() - 7)
  }

  return weeks
}

/**
 * Compute weekly word counts based on filtered members and all possible weeks
 * Aggregates daily data into weekly buckets
 * @param allPossibleWeeks - Array of week strings (Monday dates) to include
 * @param filteredMembers - Array of members to aggregate word counts from
 * @returns Array of objects with week and wordcount, including weeks with 0 words
 */
export const computeWeeklyWordCounts = (
  allPossibleWeeks: string[],
  filteredMembers: Array<{ daily_word_counts?: Array<{ day?: string; wordcount: number }> }>
): Array<{ week: string; wordcount: number }> => {
  if (allPossibleWeeks.length === 0) {
    return []
  }

  // Build a map of week -> wordcount by aggregating daily data
  const weekMap = new Map<string, number>()

  filteredMembers.forEach((member) => {
    member.daily_word_counts?.forEach((dayData) => {
      if (dayData.day) {
        const weekKey = getWeekKeyFromDay(dayData.day)

        if (!weekKey) {
          return
        }

        // Add to week total
        const currentCount = weekMap.get(weekKey) || 0
        weekMap.set(weekKey, currentCount + dayData.wordcount)
      }
    })
  })

  // Create dataset using all possible weeks
  return allPossibleWeeks.map((week) => ({
    week,
    wordcount: weekMap.get(week) || 0,
  }))
}

/**
 * Compute all possible days by generating a range from furthest future to furthest past
 * @param members - Array of members with daily_word_counts
 * @returns Array of day strings in YYYY-MM-DD format, sorted from most recent to oldest
 */
export const computeAllPossibleDays = (
  members: Array<{ daily_word_counts?: Array<{ day?: string; wordcount: number }> }>
): string[] => {
  // Find the earliest and latest dates from all members' daily data
  let earliestDay: string | null = null
  let latestDay: string | null = null

  members.forEach((member) => {
    member.daily_word_counts?.forEach((dayData) => {
      if (dayData.day) {
        if (!earliestDay || dayData.day < earliestDay) {
          earliestDay = dayData.day
        }
        if (!latestDay || dayData.day > latestDay) {
          latestDay = dayData.day
        }
      }
    })
  })

  if (!earliestDay || !latestDay) {
    return []
  }

  // Generate all days from latest to earliest
  const days: string[] = []
  const currentDate = new Date(latestDay + 'T00:00:00')
  const earliestDate = new Date(earliestDay + 'T00:00:00')

  while (currentDate >= earliestDate) {
    // Format as YYYY-MM-DD
    const year = currentDate.getFullYear()
    const month = String(currentDate.getMonth() + 1).padStart(2, '0')
    const day = String(currentDate.getDate()).padStart(2, '0')
    days.push(`${year}-${month}-${day}`)

    // Go back 1 day
    currentDate.setDate(currentDate.getDate() - 1)
  }

  return days
}

/**
 * Compute daily word counts based on filtered members and all possible days
 * @param allPossibleDays - Array of day strings to include
 * @param filteredMembers - Array of members to aggregate word counts from
 * @returns Array of objects with day and wordcount, including days with 0 words
 */
export const computeDailyWordCounts = (
  allPossibleDays: string[],
  filteredMembers: Array<{ daily_word_counts?: Array<{ day?: string; wordcount: number }> }>
): Array<{ day: string; wordcount: number }> => {
  if (allPossibleDays.length === 0) {
    return []
  }

  // Build a map of day -> wordcount based on filtered members
  const dayMap = new Map<string, number>()
  filteredMembers.forEach((member) => {
    member.daily_word_counts?.forEach((dayData) => {
      if (dayData.day) {
        const currentCount = dayMap.get(dayData.day) || 0
        dayMap.set(dayData.day, currentCount + dayData.wordcount)
      }
    })
  })

  // Create dataset using all possible days
  return allPossibleDays.map((day) => ({
    day,
    wordcount: dayMap.get(day) || 0,
  }))
}

/**
 * Format day string (YYYY-MM-DD) to readable date
 * Example: "2025-11-23" -> "Nov 23"
 */
export const formatDayToDate = (dayStr: string, locale = 'en-US'): string => {
  try {
    const date = new Date(dayStr + 'T00:00:00')

    if (!isNaN(date.getTime())) {
      const monthFull = date.toLocaleDateString(locale, { month: 'short' })
      const day = date.getDate()
      return `${monthFull} ${day}`
    }

    return dayStr
  } catch {
    return dayStr
  }
}

/**
 * Calculate trend based on visible data (works for both daily and weekly)
 */
export const calculateTrendGeneric = (
  visibleData: Array<{ wordcount: number }>
): { percentage: number; direction: 'up' | 'down' | 'neutral'; isPositive: boolean } => {
  if (visibleData.length < 3) {
    return { percentage: 0, direction: 'neutral' as const, isPositive: false }
  }

  // Compare last period (index 1) to 2 periods ago (index 2) in the visible range
  const lastPeriod = visibleData[1]?.wordcount || 0
  const twoPeriodsAgo = visibleData[2]?.wordcount || 0

  if (twoPeriodsAgo === 0) {
    return { percentage: 0, direction: 'neutral' as const, isPositive: false }
  }

  const percentageChange = ((lastPeriod - twoPeriodsAgo) / twoPeriodsAgo) * 100
  const isPositive = percentageChange > 0

  return {
    percentage: Math.abs(percentageChange),
    direction: isPositive ? ('up' as const) : ('down' as const),
    isPositive,
  }
}

/**
 * Get date range string from visible data (works for both daily and weekly)
 */
export const getDateRangeGeneric = (
  visibleData: Array<{ day?: string; week?: string; wordcount: number }>,
  formatter: (dateStr: string) => string
): string => {
  if (visibleData.length === 0) {
    return 'No data available'
  }

  const dates = visibleData.map((d) => d.day || d.week).filter(Boolean) as string[]

  if (dates.length === 0) {
    return 'No data available'
  }

  const sortedDates = [...dates].sort()
  const oldestDate = sortedDates[0]
  const newestDate = sortedDates[sortedDates.length - 1]

  if (oldestDate === newestDate) {
    return formatter(newestDate)
  }

  return `${formatter(oldestDate)} - ${formatter(newestDate)}`
}

export function buildUsageCSV(members: EnterpriseUserUsage[]): string {
  // Collect all unique dates across all members
  const dateSet = new Set<string>()
  for (const member of members) {
    for (const entry of member.daily_word_counts) {
      dateSet.add(entry.day)
    }
  }
  const sortedDates = Array.from(dateSet).sort().reverse()

  // Build lookup: userId -> day -> wordcount
  const wordsByUserAndDay = new Map<string, Map<string, number>>()
  for (const member of members) {
    const dayMap = new Map<string, number>()
    for (const entry of member.daily_word_counts) {
      dayMap.set(entry.day, entry.wordcount)
    }
    wordsByUserAndDay.set(member.user_id, dayMap)
  }

  // Rows = members, Columns = dates
  const headerRow = ['Name', ...sortedDates].join(',')

  const dataRows = members.map((m) => {
    const name = [m.first_name, m.last_name].filter(Boolean).join(' ') || 'Unknown'
    const label = escapeCSV(`${name} (${m.email || 'no email'})`)
    const dayMap = wordsByUserAndDay.get(m.user_id)
    const dayCounts = sortedDates.map((date) => {
      const count = dayMap?.get(date) ?? 0
      return String(count)
    })
    return [label, ...dayCounts].join(',')
  })

  return [headerRow, ...dataRows].join('\n')
}

export function exportUsageCSV(members: EnterpriseUserUsage[]): void {
  const csv = buildUsageCSV(members)
  downloadBlob(
    new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
    `usage-export-${new Date().toISOString().split('T')[0]}.csv`
  )
}
